import React, { createContext, useState, useEffect, useLayoutEffect, useContext } from 'react';
import storage from '../platform/storage';

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Check if they have a system preference
  const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  
  // System preference until the stored preference is loaded; App.js holds
  // rendering until `hydrated` is true
  const [isDarkMode, setIsDarkMode] = useState(prefersDark);
  const [hydrated, setHydrated] = useState(false);

  // Load the stored preference on mount
  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      const storedTheme = await storage.getItem('uiTheme');
      if (cancelled) return;
      if (storedTheme) {
        setIsDarkMode(storedTheme === 'dark');
      }
      setHydrated(true);
    };

    hydrate();
    return () => { cancelled = true; };
  }, []);

  // Update body class and storage when theme changes. A layout effect, so the
  // class is on the body before the browser paints the commit that opens the
  // hydration gate (no light frame for dark-mode users).
  useLayoutEffect(() => {
    // Writing before hydration would overwrite the stored preference
    if (!hydrated) return;
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
      storage.setItem('uiTheme', 'dark');
    } else {
      document.body.classList.remove('dark-mode');
      storage.setItem('uiTheme', 'light');
    }
  }, [hydrated, isDarkMode]);

  // Toggle theme function
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, hydrated }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Add this custom hook to easily use the theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
